import { useEffect } from 'react';
import { PARENT_SITE_ORIGIN, parentSiteUrl } from '../utils/brandRouting';
import { SOCIAL_LINKS, STUDIO, SUPPORT_EMAIL } from '../data/siteContent';

/**
 * Organization/WebSite structured data (Release 2.0.0, task T06).
 *
 * Scope decision D-06a: Organization and WebSite only. No per-app
 * SoftwareApplication entry, and deliberately no aggregateRating, review,
 * offers, or price — a rating or price the site cannot verify at publish time
 * would be a false public claim under the release charter.
 *
 * Every value traces to data/siteContent.js or the parent-site origin in
 * utils/brandRouting.js; nothing here is authored independently. The block is
 * injected once from App, not per page, so there is only ever one copy.
 */
export const STRUCTURED_DATA_ID = 'tickleapps-structured-data';

// The site's own icon, served from public/icons by every host.
export const ORGANIZATION_LOGO = parentSiteUrl('/icons/icon-512.png');

export function buildStructuredData() {
  const organization = {
    '@type': 'Organization',
    '@id': `${PARENT_SITE_ORIGIN}/#organization`,
    name: STUDIO.name,
    url: `${PARENT_SITE_ORIGIN}/`,
    logo: ORGANIZATION_LOGO,
    sameAs: SOCIAL_LINKS.map((link) => link.href),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: SUPPORT_EMAIL,
      },
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': `${PARENT_SITE_ORIGIN}/#website`,
        name: STUDIO.name,
        url: `${PARENT_SITE_ORIGIN}/`,
        publisher: { '@id': organization['@id'] },
      },
    ],
  };
}

/**
 * Renders nothing. It keeps exactly one ld+json script in <head>, reusing the
 * existing element if a previous mount created it.
 */
export default function StructuredData() {
  useEffect(() => {
    let script = document.head.querySelector(`script#${STRUCTURED_DATA_ID}`);
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', STRUCTURED_DATA_ID);
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildStructuredData());
  }, []);

  return null;
}
